import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertCircle, FiEdit2, FiMove, FiPlus, FiSave, FiTrash2, FiX, FiMinusCircle } from 'react-icons/fi';
import { Button, Checkbox, DatePicker, Input, Select, Switch, message, Modal } from 'antd';
import { isEmployeeRole } from '../utils/companyRoles';
import { apiFetch } from '../api';
import {
  FIELD_TYPES,
  arrayMove,
  sortByOrder,
  matchesSectionId,
  getFieldsForSection,
  reorderFieldsGlobally,
  computeFieldDropIndex,
  moveFieldAcrossSections,
  createDragPayload,
  parseDragPayload,
} from '../utils/formBuilderDnd';

const createSlug = (value, fallback = 'item') => {
  const slug = (value || fallback)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  return slug || fallback;
};

const createSectionId = (title, index = 0) => `${createSlug(title, 'section')}_${index + 1}`;

const createFieldKey = (label, index) => `${createSlug(label, 'field')}_${index + 1}`;

const createEmptySection = () => ({ title: '', enabled: true, optional: false });

const CORE_FIELD_KEYS = new Set(['firstName', 'email', 'mobileNumber', 'hostId']);

const createEmptyField = (sectionId = '') => ({
  label: '',
  key: '',
  sectionId,
  type: 'textbox',
  placeholder: '',
  required: false,
  enabled: true,
  optionsText: '',
  linkedEmployeeFieldKey: '',
});

const toDraftSection = (section, index) => ({
  _id: section._id,
  clientId: section.id || section._id || `section-${index}`,
  id: section.id || createSectionId(section.title, index),
  title: section.title || '',
  order: Number.isFinite(section.order) ? section.order : index,
  enabled: section.enabled !== false,
  optional: Boolean(section.optional),
  system: Boolean(section.system),
});

const toDraftField = (field, index) => ({
  _id: field._id,
  clientId: field._id || `${field.key || 'field'}-${index}`,
  key: field.key || createFieldKey(field.label, index),
  label: field.label || '',
  sectionId: field.sectionId || '',
  type: FIELD_TYPES.some((option) => option.value === field.type) ? field.type : 'textbox',
  placeholder: field.placeholder || '',
  required: Boolean(field.required),
  enabled: field.enabled !== false,
  optionsText: Array.isArray(field.options) ? field.options.join(', ') : '',
  order: Number.isFinite(field.order) ? field.order : index,
  system: Boolean(field.system),
  linkedEmployeeFieldKey: field.linkedEmployeeFieldKey || '',
});

const normalizeFieldSectionIds = (draftSections, draftFields) => {
  const sectionAliases = new Map();

  draftSections.forEach((section) => {
    if (!section.id) return;
    sectionAliases.set(section.id, section.id);
    sectionAliases.set(section.clientId, section.id);
    if (section._id) sectionAliases.set(section._id, section.id);
  });

  return draftFields.map((field) => ({
    ...field,
    sectionId: sectionAliases.get(field.sectionId) || field.sectionId,
  }));
};

const buildSectionPayload = (sections) => sections.map((section, index) => ({
  ...(section._id ? { _id: section._id } : {}),
  id: section.id || section.clientId || createSectionId(section.title, index),
  title: section.title.trim(),
  order: index,
  enabled: section.enabled !== false,
  optional: Boolean(section.optional),
  system: Boolean(section.system),
}));

const buildFieldPayload = (sections, fields) => {
  let order = 0;

  return sections.flatMap((section) => getFieldsForSection(fields, section).map((field) => ({
    ...(field._id ? { _id: field._id } : {}),
    key: field.key || createFieldKey(field.label, order),
    label: field.label.trim(),
    sectionId: section.id || section.clientId || field.sectionId || '',
    type: field.type,
    required: Boolean(field.required),
    enabled: Boolean(field.enabled),
    options: ['dropdown', 'checklist'].includes(field.type)
      ? field.optionsText.split(',').map((option) => option.trim()).filter(Boolean)
      : [],
    placeholder: field.placeholder || '',
    order: order++,
    system: Boolean(field.system),
    linkedEmployeeFieldKey: field.linkedEmployeeFieldKey || '',
  })));
};

const VisitorFormConfiguration = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role?.toLowerCase();
  const isSuperadmin = role === 'superadmin';
  const isAdmin = role === 'admin' || isSuperadmin;
  const canView = isAdmin || isEmployeeRole(role);
  const canEdit = isAdmin;
  const [sections, setSections] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [sectionDraft, setSectionDraft] = useState(createEmptySection());
  const [fieldDraft, setFieldDraft] = useState(createEmptyField());
  const [dragOverSectionId, setDragOverSectionId] = useState(null);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const dragPayloadRef = useRef(null);
  const [employeeFields, setEmployeeFields] = useState([]);

  useEffect(() => {
    const fetchEmployeeFieldConfig = async () => {
      try {
        const response = await apiFetch('/api/employee-fields');
        const data = await response.json();
        if (response.ok && Array.isArray(data.data?.fields)) {
          // Include both system employee fields (firstName, department, etc.) and custom fields
          setEmployeeFields(data.data.fields.filter(f => f.enabled !== false));
        }
      } catch (error) {
        console.error('Error loading employee field config:', error);
      }
    };
    fetchEmployeeFieldConfig();
  }, []);

  const employeeFieldOptions = useMemo(() => [
    { value: '', label: '— None (no link) —' },
    ...employeeFields.map((f) => ({
      value: f.key,
      label: `${f.label} (${f.key})`,
    }))
  ], [employeeFields]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await apiFetch('/api/visitor-fields');
        const data = await response.json();
        const nextSections = sortByOrder(Array.isArray(data.data?.sections) ? data.data.sections.map(toDraftSection) : []);
        const nextFields = sortByOrder(Array.isArray(data.data?.fields) ? data.data.fields.map(toDraftField) : []);
        const normalizedNextFields = normalizeFieldSectionIds(nextSections, nextFields);

        if (nextSections.length === 0 && normalizedNextFields.length > 0) {
          const fallbackSection = { clientId: 'additional-details', id: 'additional-details', title: 'Additional Details', order: 0 };
          setSections([fallbackSection]);
          setFields(normalizedNextFields.map((field) => ({ ...field, sectionId: fallbackSection.id })));
        } else {
          setSections(nextSections);
          setFields(normalizedNextFields);
        }
      } catch (error) {
        console.error('Error loading visitor field config:', error);
        setSections([]);
        setFields([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const sectionOptions = useMemo(() => sections.map((section) => ({ value: section.id, label: section.title })), [sections]);
  const hasFormConfig = sections.length > 0 || fields.length > 0;

  const openSectionModal = (section = null) => {
    if (!canEdit) return;
    setEditingSectionId(section?.clientId || null);
    setSectionDraft(section ? { title: section.title } : createEmptySection());
    setShowSectionModal(true);
  };

  const closeSectionModal = () => {
    setShowSectionModal(false);
    setEditingSectionId(null);
    setSectionDraft(createEmptySection());
  };

  const persistConfiguration = async (nextSections = sections, nextFields = fields) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const response = await apiFetch('/api/visitor-fields', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sections: buildSectionPayload(nextSections),
          fields: buildFieldPayload(nextSections, nextFields),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Unable to save configuration');
      }

      const data = await response.json();
      const savedSections = Array.isArray(data.data?.sections) ? data.data.sections.map(toDraftSection) : [];
      const savedFields = Array.isArray(data.data?.fields) ? data.data.fields.map(toDraftField) : [];
      const normalizedSavedFields = normalizeFieldSectionIds(savedSections, savedFields);
      setSections(savedSections);
      setFields(normalizedSavedFields);
      message.success('Visitor form configuration saved successfully!');
      return { sections: savedSections, fields: normalizedSavedFields };
    } catch (error) {
      message.error(error.message || 'Unable to save configuration');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveSectionDraft = async () => {
    if (!canEdit || saving) return;
    const title = sectionDraft.title.trim();
    if (!title) return;

    const nextSections = editingSectionId
      ? sections.map((section) => (
        section.clientId === editingSectionId
          ? { ...section, title }
          : section
      ))
      : [
        ...sections,
        {
          clientId: createSectionId(title, sections.length),
          id: createSectionId(title, sections.length),
          title,
          order: sections.length,
          enabled: true,
          optional: false,
          system: false,
        },
      ];

    setSections(nextSections);
    closeSectionModal();
  };

  const removeSection = async (sectionId) => {
    if (!canEdit || saving) return;
    const section = sections.find((currentSection) => currentSection.clientId === sectionId || currentSection.id === sectionId);
    Modal.confirm({
      title: 'Are you sure you want to delete this section?',
      content: `This will permanently delete the "${section?.title || 'Section'}" section and all fields inside it.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        const sectionAliases = new Set([sectionId, section?.id, section?.clientId, section?._id].filter(Boolean));
        const nextSections = sections.filter((currentSection) => !sectionAliases.has(currentSection.clientId));
        const nextFields = fields.filter((field) => !sectionAliases.has(field.sectionId));
        setSections(nextSections);
        setFields(nextFields);
      }
    });
  };

  const openFieldModal = (field = null, sectionId = '') => {
    if (!canEdit) return;
    const targetSectionId = field?.sectionId || sectionId || sections[0]?.clientId || '';
    setEditingFieldId(field?.clientId || null);
    setFieldDraft(field ? { ...field, optionsText: field.optionsText || '' } : createEmptyField(targetSectionId));
    setShowFieldModal(true);
  };

  const closeFieldModal = () => {
    setShowFieldModal(false);
    setEditingFieldId(null);
    setFieldDraft(createEmptyField());
  };

  const saveFieldDraft = async () => {
    if (!canEdit || saving) return;
    const label = fieldDraft.label.trim();
    const sectionId = fieldDraft.sectionId || sections[0]?.clientId || '';
    const key = (fieldDraft.key || '').trim() || createFieldKey(label, fields.length);

    if (!label || !sectionId) return;

    const nextField = {
      key,
      label,
      sectionId,
      type: fieldDraft.type,
      placeholder: fieldDraft.placeholder || '',
      required: fieldDraft.required,
      enabled: fieldDraft.enabled,
      optionsText: fieldDraft.optionsText || '',
      linkedEmployeeFieldKey: fieldDraft.linkedEmployeeFieldKey || '',
    };

    const nextFields = editingFieldId
      ? fields.map((field) => (
        field.clientId === editingFieldId
          ? { ...field, ...nextField, clientId: field.clientId, _id: field._id }
          : field
      ))
      : [...fields, { ...nextField, clientId: `${key}-${Date.now()}` }];

    setFields(nextFields);
    closeFieldModal();
  };

    // Function removed to avoid auto-save

  const reorderSections = async (sourceClientId, targetClientId) => {
    if (!canEdit || saving || !sourceClientId || sourceClientId === targetClientId) return;
    const fromIndex = sections.findIndex((section) => section.clientId === sourceClientId);
    const toIndex = sections.findIndex((section) => section.clientId === targetClientId);
    if (fromIndex < 0 || toIndex < 0) return;

    const nextSections = arrayMove(sections, fromIndex, toIndex);
    const nextFields = reorderFieldsGlobally(nextSections, fields);
    setSections(nextSections);
    setFields(nextFields);
  };

  const reorderFields = async (section, sourceClientId, targetClientId, dropPosition = 'before') => {
    if (!canEdit || saving || !sourceClientId) return;
    if (sourceClientId === targetClientId && targetClientId !== '__end__') return;

    // Check if the source field belongs to the target section
    const sourceField = fields.find((f) => f.clientId === sourceClientId);
    if (!sourceField) return;

    const isSourceInSection = matchesSectionId(sourceField, section);

    if (isSourceInSection) {
      // Same-section reorder
      const sectionFields = getFieldsForSection(fields, section);
      const fromIndex = sectionFields.findIndex((field) => field.clientId === sourceClientId);
      const toIndex = computeFieldDropIndex(sectionFields, sourceClientId, targetClientId, dropPosition);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

      const reorderedSectionFields = arrayMove(sectionFields, fromIndex, toIndex);
      const sectionFieldIds = new Set(sectionFields.map((field) => field.clientId));
      const otherFields = fields.filter((field) => !sectionFieldIds.has(field.clientId));
      const nextFields = reorderFieldsGlobally(sections, [...otherFields, ...reorderedSectionFields]);

      setFields(nextFields);
    } else {
      // Cross-section move
      const nextFields = moveFieldAcrossSections(fields, sections, sourceClientId, section, targetClientId, dropPosition);
      if (!nextFields) return;

      setFields(nextFields);
    }
  };

  const isCoreSection = (section) => section.id === 'personal-details';
  const isCoreField = (field) => CORE_FIELD_KEYS.has(field.key);

  const handleSectionDragStart = (event, sectionClientId) => {
    if (!canEdit || saving) return;
    dragPayloadRef.current = { type: 'section', id: sectionClientId };
    setActiveDrag({ type: 'section', id: sectionClientId });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-vms-dnd', createDragPayload('section', sectionClientId));
    event.dataTransfer.setData('text/plain', sectionClientId);
  };

  const handleSectionDragOver = (event, sectionClientId) => {
    if (dragPayloadRef.current?.type !== 'section') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSectionId(sectionClientId);
  };

  const handleSectionDrop = async (event, targetClientId) => {
    event.preventDefault();
    const payload = parseDragPayload(event.dataTransfer.getData('application/x-vms-dnd'))
      || parseDragPayload(event.dataTransfer.getData('text/plain'));
    const sourceClientId = payload?.type === 'section' ? payload.id : null;
    clearDragState();
    if (!sourceClientId) return;
    await reorderSections(sourceClientId, targetClientId);
  };

  const handleFieldDragStart = (event, fieldClientId) => {
    if (!canEdit || saving) return;
    event.stopPropagation();
    dragPayloadRef.current = { type: 'field', id: fieldClientId };
    setActiveDrag({ type: 'field', id: fieldClientId });
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/x-vms-dnd', createDragPayload('field', fieldClientId));
    event.dataTransfer.setData('text/plain', fieldClientId);
  };

  const handleFieldDragOver = (event, fieldClientId) => {
    if (dragPayloadRef.current?.type !== 'field') return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setDragOverFieldId(fieldClientId);
  };

  // Allow field drops on section containers (for cross-section moves and empty sections)
  const handleSectionFieldDragOver = (event, sectionClientId) => {
    if (dragPayloadRef.current?.type !== 'field') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSectionId(sectionClientId);
  };

  const handleSectionFieldDrop = async (event, section) => {
    event.preventDefault();
    const payload = parseDragPayload(event.dataTransfer.getData('application/x-vms-dnd'))
      || parseDragPayload(event.dataTransfer.getData('text/plain'));
    const sourceClientId = payload?.type === 'field' || payload?.type === 'legacy' ? payload.id : null;
    clearDragState();
    if (!sourceClientId) return;
    await reorderFields(section, sourceClientId, '__end__', 'before');
  };

  const handleFieldDrop = async (event, section, targetClientId, dropPosition = 'before') => {
    event.preventDefault();
    event.stopPropagation();
    const payload = parseDragPayload(event.dataTransfer.getData('application/x-vms-dnd'))
      || parseDragPayload(event.dataTransfer.getData('text/plain'));
    const sourceClientId = payload?.type === 'field' || payload?.type === 'legacy' ? payload.id : null;
    clearDragState();
    if (!sourceClientId) return;
    await reorderFields(section, sourceClientId, targetClientId, dropPosition);
  };

  const clearDragState = () => {
    dragPayloadRef.current = null;
    setActiveDrag(null);
    setDragOverSectionId(null);
    setDragOverFieldId(null);
  };

  const updateSection = (clientId, patch) => {
    if (!canEdit) return;
    setSections((currentSections) => currentSections.map((section) => (
      section.clientId === clientId ? { ...section, ...patch } : section
    )));
  };

  const updateField = (clientId, patch) => {
    if (!canEdit) return;
    setFields((currentFields) => currentFields.map((field) => (
      field.clientId === clientId ? { ...field, ...patch } : field
    )));
  };

  const removeField = async (clientId) => {
    if (!canEdit || saving) return;
    const field = fields.find((f) => f.clientId === clientId);
    Modal.confirm({
      title: 'Are you sure you want to delete this field?',
      content: `This will permanently delete the "${field?.label || 'Field'}" field from the layout.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        const nextFields = fields.filter((f) => f.clientId !== clientId);
        setFields(nextFields);
      }
    });
  };

  const saveConfiguration = async () => {
    if (!canEdit || saving) return;
    await persistConfiguration(sections, fields);
  };

  const renderFieldPreview = (field) => {
    if (field.type === 'dropdown') {
      const options = field.optionsText.split(',').map((option) => option.trim()).filter(Boolean);

      return (
        <Select
          disabled
          className="w-full h-10"
          placeholder={field.placeholder || 'Select option'}
          options={(options.length ? options : ['Option']).map((option) => ({ value: option, label: option }))}
        />
      );
    }

    if (field.type === 'checklist') {
      const options = field.optionsText.split(',').map((option) => option.trim()).filter(Boolean);

      return (
        <div className="min-h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="flex flex-col gap-2">
            {(options.length ? options : ['Option']).slice(0, 3).map((option) => (
              <Checkbox key={option} disabled>{option}</Checkbox>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'date' || field.type === 'datetime') {
      const isDateTime = field.type === 'datetime';
      return (
        <DatePicker
          disabled
          className="w-full h-10"
          format={isDateTime ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY"}
          placeholder={field.placeholder || (isDateTime ? 'Select date and time' : 'Select date')}
        />
      );
    }

    return (
      <Input
        disabled
        value=""
        placeholder={field.placeholder || field.label}
        className="h-10 rounded-md bg-slate-50 dark:bg-slate-900"
      />
    );
  };

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
        <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-full flex items-center justify-center mb-6">
          <FiAlertCircle className="w-10 h-10" />
        </div>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">Access Denied</h2>
        <p className="text-slate-500 max-w-sm">Only administrators and employees can view visitor field configuration.</p>
      </div>
    );
  }

  return (
    <div className="wm-page pb-8">
      <div className="wm-page-header">
        <div>
          <h1 className="wm-page-title dark:text-white">Visitor Form Builder</h1>
          <p className="wm-page-subtitle dark:text-slate-400">Edit visitor sections and fields, then save the layout used by the add visitor page.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            type="default"
            icon={<FiPlus />}
            onClick={() => openSectionModal()}
            disabled={!canEdit || saving}
            className="h-10 px-4 rounded-lg border-slate-200 text-slate-700 dark:border-white/10 dark:text-slate-200"
          >
            Add Section
          </Button>
          <Button
            type="primary"
            icon={<FiPlus />}
            onClick={() => (sections.length > 0 ? openFieldModal(null, sections[0].clientId) : openSectionModal())}
            disabled={!canEdit || saving}
            className="wm-btn-primary h-10 px-5"
          >
            Add Field
          </Button>
          <Button
            icon={<FiSave />}
            type="primary"
            onClick={saveConfiguration}
            loading={saving}
            disabled={!canEdit}
            className="wm-btn-primary h-10 px-5"
          >
            Save Changes
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {loading ? (
          <div className="wm-card-flat dark:bg-vms-navy dark:border-white/10 p-8 text-slate-500 dark:text-slate-400">Loading configuration...</div>
        ) : !hasFormConfig ? (
          <div className="wm-card-flat dark:bg-vms-navy dark:border-white/10 p-10 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 dark:text-slate-300">
              <FiPlus className="w-8 h-8" />
            </div>
            <div>
              <h2 className="wm-section-title">No sections yet</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Add a section first, then place fields inside it.</p>
            </div>
            <Button type="primary" onClick={() => openSectionModal()} className="wm-btn-primary h-10 px-5">
              Add Section
            </Button>
          </div>
        ) : (
          <div className="wm-card-flat overflow-hidden dark:bg-vms-navy dark:border-white/10">
            <div className="border-b border-slate-100 px-4 py-3 dark:border-white/5 md:px-5">
              <h2 className="wm-section-title">Visitor Form Layout</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Saved sections and fields are shown below exactly where visitors will see them.</p>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {sections.map((section) => {
                const sectionFields = getFieldsForSection(fields, section);
                const sectionEnabled = section.enabled !== false;
                const isDraggingSection = activeDrag?.type === 'section' && activeDrag.id === section.clientId;
                const isSectionDropTarget = dragOverSectionId === section.clientId && activeDrag?.type === 'section' && activeDrag.id !== section.clientId;

                return (
                  <div
                    key={section.clientId}
                    className={`bg-white dark:bg-transparent transition-colors ${isDraggingSection ? 'opacity-50' : ''} ${isSectionDropTarget ? 'ring-2 ring-inset ring-[#0070b1]/40' : ''}`}
                    onDragOver={(event) => {
                      handleSectionDragOver(event, section.clientId);
                      handleSectionFieldDragOver(event, section.clientId);
                    }}
                    onDrop={(event) => {
                      if (dragPayloadRef.current?.type === 'section') {
                        handleSectionDrop(event, section.clientId);
                      } else if (dragPayloadRef.current?.type === 'field') {
                        handleSectionFieldDrop(event, section);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverSectionId === section.clientId) setDragOverSectionId(null);
                    }}
                  >
                    <div className="flex min-h-14 items-center justify-between gap-3 px-4 py-3 dark:border-white/5 md:px-5">
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          draggable={canEdit && !saving}
                          onDragStart={(event) => handleSectionDragStart(event, section.clientId)}
                          onDragEnd={clearDragState}
                          className={`shrink-0 ${canEdit && !saving ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
                          title="Drag to reorder section"
                        >
                          <FiMove className="text-lg text-slate-400" aria-hidden="true" />
                        </span>
                        <Input
                          value={section.title}
                          onChange={(event) => updateSection(section.clientId, { title: event.target.value })}
                          disabled={!canEdit || saving}
                          variant="borderless"
                          className="max-w-lg px-0 text-lg font-semibold text-[#00456f] dark:text-white"
                          placeholder="Section title"
                        />
                        <div className="flex shrink-0 items-center gap-2">
                          {!(isCoreSection(section) && !isSuperadmin) && (
                            <Switch
                              checked={sectionEnabled}
                              size="small"
                              disabled={!canEdit || saving}
                              onChange={(checked) => updateSection(section.clientId, { enabled: checked })}
                              title={sectionEnabled ? 'Section visible on register form' : 'Section hidden on register form'}
                            />
                          )}
                          {section.optional ? (
                            <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:inline">Optional</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {!(isCoreSection(section) && !isSuperadmin) && (
                          <>
                            <Button
                              type="text"
                              aria-label="Edit section"
                              icon={<FiEdit2 />}
                              onClick={() => openSectionModal(section)}
                              disabled={!canEdit || saving}
                              className="h-8 w-8 text-slate-600 dark:text-slate-300"
                            />
                            <Button
                              danger
                              type="text"
                              aria-label="Delete section"
                              icon={<FiTrash2 />}
                              onClick={() => removeSection(section.clientId)}
                              disabled={!canEdit || saving}
                              className="h-8 w-8"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    <div className={`border-t border-slate-100 px-4 py-5 dark:border-white/5 md:px-5 ${sectionEnabled ? '' : 'opacity-50'}`}>
                      {sectionFields.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => openFieldModal(null, section.clientId)}
                          disabled={!canEdit || saving}
                          className="w-full rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm font-medium text-slate-500 transition hover:border-[#0070b1] hover:text-[#0070b1] disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400"
                        >
                          Add a field to this section
                        </button>
                      ) : (
                        <div className="grid grid-cols-1 gap-x-7 gap-y-8 lg:grid-cols-2 xl:grid-cols-3">
                          <AnimatePresence initial={false}>
                            {sectionFields.map((field) => {
                              const isDraggingField = activeDrag?.type === 'field' && activeDrag.id === field.clientId;
                              const isFieldDropTarget = dragOverFieldId === field.clientId
                                && activeDrag?.type === 'field'
                                && activeDrag.id !== field.clientId;

                              return (
                                <motion.div
                                  key={field.clientId}
                                  layout={!activeDrag}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  className={`min-w-0 transition-colors ${isDraggingField ? 'opacity-50' : ''} ${isFieldDropTarget ? 'rounded-lg ring-2 ring-[#0070b1]/40' : ''}`}
                                  onDragOver={(event) => handleFieldDragOver(event, field.clientId)}
                                  onDrop={(event) => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    const position = (event.clientY - rect.top) > (rect.height / 2) ? 'after' : 'before';
                                    handleFieldDrop(event, section, field.clientId, position);
                                  }}
                                  onDragLeave={() => {
                                    if (dragOverFieldId === field.clientId) setDragOverFieldId(null);
                                  }}
                                >
                                  <div
                                    className="mb-2 flex min-h-7 items-center justify-between gap-3"
                                    draggable={canEdit && !saving}
                                    onDragStart={(event) => handleFieldDragStart(event, field.clientId)}
                                    onDragEnd={clearDragState}
                                  >
                                    <div className="flex min-w-0 items-center gap-2">
                                      <span
                                        className={`shrink-0 ${canEdit && !saving ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
                                        title="Drag to reorder field"
                                      >
                                        <FiMove className="text-base text-slate-400" aria-hidden="true" />
                                      </span>
                                      <span className="truncate text-sm font-semibold text-[#545454] dark:text-slate-200">
                                        {field.label || 'Untitled Field'} {field.required ? <span className="text-red-500">*</span> : null}
                                      </span>
                                      {field.linkedEmployeeFieldKey && (
                                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30" title={`Linked to employee field: ${field.linkedEmployeeFieldKey}`}>
                                          🔗 {field.linkedEmployeeFieldKey}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                      {!(isCoreField(field) && !isSuperadmin) && (
                                        <Switch
                                          checked={field.enabled}
                                          size="small"
                                          onChange={(checked) => updateField(field.clientId, { enabled: checked })}
                                          disabled={!canEdit || saving}
                                          title={field.enabled ? 'Field visible on register form' : 'Field hidden on register form'}
                                        />
                                      )}
                                      {!(isCoreField(field) && !isSuperadmin) && (
                                        <>
                                          <Button
                                            type="text"
                                            aria-label="Edit field"
                                            icon={<FiEdit2 />}
                                            onClick={() => openFieldModal(field, section.clientId)}
                                            disabled={!canEdit || saving}
                                            className="h-8 w-8 text-slate-600 dark:text-slate-300"
                                          />
                                          <Button
                                            danger
                                            type="text"
                                            aria-label="Delete field"
                                            icon={<FiTrash2 />}
                                            onClick={() => removeField(field.clientId)}
                                            disabled={!canEdit || saving}
                                            className="h-8 w-8"
                                          />
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  <div className={field.enabled && sectionEnabled ? '' : 'opacity-50'}>
                                    {renderFieldPreview(field)}
                                  </div>
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>
                          {canEdit && sectionFields.length > 0 ? (
                            <div
                              className={`min-h-12 rounded-lg border border-dashed transition-colors lg:col-span-2 xl:col-span-3 ${dragOverFieldId === '__end__' ? 'border-[#0070b1] bg-[#0070b1]/5' : 'border-transparent'
                                }`}
                              onDragOver={(event) => {
                                if (dragPayloadRef.current?.type !== 'field') return;
                                event.preventDefault();
                                event.stopPropagation();
                                setDragOverFieldId('__end__');
                              }}
                              onDrop={(event) => handleFieldDrop(event, section, '__end__', 'before')}
                              onDragLeave={() => {
                                if (dragOverFieldId === '__end__') setDragOverFieldId(null);
                              }}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showSectionModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSectionModal}
              className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="relative w-full sm:max-w-lg max-h-[90dvh] flex flex-col bg-white dark:bg-vms-navy rounded-t-3xl sm:rounded-3xl shadow-[0_24px_60px_rgba(15,23,42,0.22)] overflow-hidden border border-slate-100 dark:border-white/10"
            >
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-3 bg-slate-50/60 dark:bg-white/5 shrink-0">
                <div>
                  <h2 className="text-xl font-medium text-slate-800 dark:text-white">{editingSectionId ? 'Edit Section' : 'Add Section'}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Create a grouping for visitor fields.</p>
                </div>
                <Button
                  onClick={closeSectionModal}
                  type="text"
                  icon={<FiX />}
                  className="h-10 w-10 rounded-full text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
                />
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                <div className="space-y-1">
                  <label className="wm-label">Section Title</label>
                  <Input
                    value={sectionDraft.title}
                    onChange={(event) => setSectionDraft((current) => ({ ...current, title: event.target.value }))}
                    disabled={!canEdit}
                    placeholder="e.g. Additional Details"
                    className="h-10 rounded-lg"
                  />
                </div>
              </div>

              <div className="px-4 sm:px-6 py-4 border-t border-slate-100 dark:border-white/5 flex flex-wrap items-center justify-end gap-3 bg-slate-50/60 dark:bg-white/5 shrink-0">
                <Button onClick={closeSectionModal} className="h-10 px-5 rounded-lg">
                  Cancel
                </Button>
                <Button type="primary" onClick={saveSectionDraft} loading={saving} disabled={!canEdit} className="wm-btn-primary h-10 px-5">
                  {editingSectionId ? 'Update Section' : 'Add Section'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFieldModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeFieldModal}
              className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              className="relative w-full sm:max-w-lg max-h-[90dvh] flex flex-col bg-white dark:bg-vms-navy rounded-t-3xl sm:rounded-3xl shadow-[0_24px_60px_rgba(15,23,42,0.22)] overflow-hidden border border-slate-100 dark:border-white/10"
            >
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-3 bg-slate-50/60 dark:bg-white/5 shrink-0">
                <div className="min-w-0 pr-2">
                  <h2 className="text-lg sm:text-xl font-medium text-slate-800 dark:text-white">{editingFieldId ? 'Edit Field' : 'Add Field'}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Add a field inside one of your sections.</p>
                </div>
                <Button
                  onClick={closeFieldModal}
                  type="text"
                  icon={<FiX />}
                  className="h-10 w-10 rounded-full text-slate-500 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
                />
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                <div className="space-y-1">
                  <label className="wm-label">Field Label</label>
                  <Input
                    value={fieldDraft.label}
                    onChange={(event) => {
                      const newLabel = event.target.value;
                      setFieldDraft((current) => ({ 
                        ...current, 
                        label: newLabel, 
                        key: editingFieldId ? current.key : createSlug(newLabel, 'field') 
                      }));
                    }}
                    disabled={!canEdit}
                    placeholder="e.g. Full Name"
                    className="h-10 rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="wm-label">Field Name</label>
                  <Input
                    value={fieldDraft.key}
                    onChange={(event) => setFieldDraft((current) => ({ ...current, key: event.target.value }))}
                    disabled={true}
                    placeholder="e.g. full_name"
                    className="h-10 rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="wm-label">Section</label>
                  <Select
                    className="w-full h-10"
                    value={fieldDraft.sectionId || undefined}
                    onChange={(value) => setFieldDraft((current) => ({ ...current, sectionId: value }))}
                    disabled={!canEdit}
                    options={sectionOptions}
                    placeholder="Select section"
                  />
                </div>

                <div className="space-y-1">
                  <label className="wm-label">Field Type</label>
                  <Select
                    className="w-full h-10"
                    value={fieldDraft.type}
                    onChange={(value) => setFieldDraft((current) => ({ ...current, type: value }))}
                    disabled={!canEdit}
                    options={FIELD_TYPES}
                  />
                </div>

                <div className="space-y-1">
                  <label className="wm-label">Placeholder Text</label>
                  <Input
                    value={fieldDraft.placeholder}
                    onChange={(event) => setFieldDraft((current) => ({ ...current, placeholder: event.target.value }))}
                    disabled={!canEdit}
                    placeholder="Enter placeholder text"
                    className="h-10 rounded-lg"
                  />
                </div>

                {['dropdown', 'checklist'].includes(fieldDraft.type) && (() => {
                  const optionsList = fieldDraft.optionsText 
                    ? fieldDraft.optionsText.split(',').map(opt => opt.trimStart()) 
                    : [''];
                  
                  return (
                    <div className="space-y-3">
                      <label className="wm-label">Options</label>
                      <div className="space-y-2">
                        {optionsList.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              value={opt}
                              onChange={(e) => {
                                const newOpts = [...optionsList];
                                newOpts[idx] = e.target.value;
                                setFieldDraft(curr => ({ ...curr, optionsText: newOpts.join(',') }));
                              }}
                              placeholder={`Option ${idx + 1}`}
                              className="h-10 rounded-lg flex-1"
                              disabled={!canEdit}
                            />
                            <Button
                              type="text"
                              danger
                              icon={<FiMinusCircle className="w-5 h-5" />}
                              onClick={() => {
                                const newOpts = optionsList.filter((_, i) => i !== idx);
                                setFieldDraft(curr => ({ ...curr, optionsText: newOpts.join(',') }));
                              }}
                              disabled={!canEdit || optionsList.length <= 1}
                              className="flex items-center justify-center px-2"
                            />
                          </div>
                        ))}
                      </div>
                      <Button
                        type="dashed"
                        icon={<FiPlus />}
                        onClick={() => {
                          const newOpts = [...optionsList, ''];
                          setFieldDraft(curr => ({ ...curr, optionsText: newOpts.join(',') }));
                        }}
                        disabled={!canEdit}
                        className="w-full sm:w-auto h-10 rounded-lg"
                      >
                        Add Option
                      </Button>
                    </div>
                  );
                })()}
                {!isCoreField(fieldDraft) && (
                  <div className="space-y-1">
                    <label className="wm-label">Link to Employee Field</label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">When a host is selected, this visitor field will auto-fill with the linked employee field value.</p>
                    <Select
                      className="w-full h-10"
                      value={fieldDraft.linkedEmployeeFieldKey || ''}
                      onChange={(value) => setFieldDraft((current) => ({ ...current, linkedEmployeeFieldKey: value || '' }))}
                      disabled={!canEdit}
                      options={employeeFieldOptions}
                      placeholder="Select employee field to link"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Required</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Mark this field as mandatory</div>
                  </div>
                  <Switch
                    checked={fieldDraft.required}
                    onChange={(checked) => setFieldDraft((current) => ({ ...current, required: checked }))}
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <div className="px-4 sm:px-6 py-4 border-t border-slate-100 dark:border-white/5 flex flex-wrap items-center justify-end gap-3 bg-slate-50/60 dark:bg-white/5 shrink-0">
                <Button onClick={closeFieldModal} className="h-10 px-5 rounded-lg">
                  Cancel
                </Button>
                <Button type="primary" onClick={saveFieldDraft} loading={saving} disabled={!canEdit} className="wm-btn-primary h-10 px-5">
                  {editingFieldId ? 'Update Field' : 'Add Field'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisitorFormConfiguration;
